import { Code, Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonDialog } from 'components/common/dialogs';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonFormGrid } from 'components/common/form/grid';
import { ErrorBoundary } from 'components/common/error-boundary';
import env from 'config';
import { FeatureFlagHelper } from 'lib_ts/classes/feature-flag.helper';
import {
  FEATURE_FLAG_ENVIRONMENT_OPTIONS,
  FeatureFlagEnvironment,
} from 'lib_ts/enums/feature-flags.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IFeatureFlag } from 'lib_ts/interfaces/feature-flags/i-feature-flag';
import { useMemo, useState } from 'react';
import { PlatformConsoleService } from 'services/platform-console.service';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'FeatureFlagDuplicateDialog';

interface IProps {
  flag: IFeatureFlag;
  onClose: (refresh: boolean) => void;
}

export const FeatureFlagDuplicateDialog = (props: IProps) => {
  const currentUserID = useAuthStore((s) => s.current.userID);

  const isOwnedByOther = !FeatureFlagHelper.isOwnedBy(
    props.flag,
    currentUserID
  );

  // exclude the flag's own environment (can only be duplicated elsewhere) and
  // any environment this deployment has no access to manage
  const options = useMemo(() => {
    const manageable = FeatureFlagHelper.getManageableEnvironments(
      env.identifier
    );

    return FEATURE_FLAG_ENVIRONMENT_OPTIONS.filter(
      (o) =>
        o.value !== props.flag.environment &&
        manageable.includes(o.value as FeatureFlagEnvironment)
    );
  }, [props.flag.environment]);

  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState<FeatureFlagEnvironment>(
    options[0]?.value as FeatureFlagEnvironment
  );

  const handleDuplicate = async () => {
    if (loading) {
      return;
    }

    if (!target) {
      NotifyHelper.error({
        message_md: 'Please select a target environment and try again.',
      });
      return;
    }

    setLoading(true);

    try {
      const result =
        await PlatformConsoleService.getInstance().duplicateFeatureFlag(
          props.flag._id,
          target
        );

      if (!result?.success) {
        NotifyHelper.error({
          message_md: result?.error ?? 'Failed to duplicate the feature flag.',
        });
        return;
      }

      NotifyHelper.success({
        message_md: `Feature flag \`${props.flag.name}\` duplicated to ${target}.`,
      });

      props.onClose(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        width={RADIX.DIALOG.WIDTH.MD}
        title="Duplicate Feature Flag"
        loading={loading}
        content={
          <CommonFormGrid columns={1}>
            <Flex direction="column" gap="1">
              <Text>
                Copy <Code>{props.flag.name}</Code> (description, enabled state,
                and targeting criteria) into another environment.
              </Text>
              {isOwnedByOther && (
                <Text color={RADIX.COLOR.WARNING}>
                  This flag is owned by{' '}
                  <Code>{props.flag.createdBy?.email ?? 'someone else'}</Code>.
                </Text>
              )}
            </Flex>

            <CommonSelectInput
              id="feature-flag-duplicate-target"
              label="Target environment"
              name="target-environment"
              disabled={loading}
              options={options}
              value={target}
              onChange={(v) => setTarget(v as FeatureFlagEnvironment)}
            />
          </CommonFormGrid>
        }
        buttons={[
          {
            label: 'common.duplicate',
            color: RADIX.COLOR.ACCENT,
            variant: 'solid',
            loading,
            onClick: handleDuplicate,
          },
        ]}
        onClose={() => props.onClose(false)}
      />
    </ErrorBoundary>
  );
};
